import React from "react";
import { observer } from "mobx-react";
import { AuthorComposerUpdateStore } from "../../store";
import { AuthorComposerPublicUpdateForm } from "./AuthorComposerPublicUpdateForm";
import { ErrorDialog, ConfirmCancelDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { OutdatedAfterSaveNotice } from "../../../../common/controls/forms/OutdatedAfterSaveNotice";
import { Gender } from "../../../../api/graphql";
import { TaxIdType } from "../../../../api/graphql";

export type AuthorComposerPublicUpdateProps = {
  authorComposerUpdateStore: AuthorComposerUpdateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const AuthorComposerPublicUpdate: React.FC<AuthorComposerPublicUpdateProps> =
  observer(
    ({ authorComposerUpdateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={authorComposerUpdateStore.isSaving} />
          <OutdatedAfterSaveNotice
            open={authorComposerUpdateStore.savedButOutdated}
            onReload={() => authorComposerUpdateStore.reloadAuthorComposer()}
            reloadDisabled={authorComposerUpdateStore.isProcessing}
          />
          <AuthorComposerPublicUpdateForm
            authorComposerUpdateStore={authorComposerUpdateStore}
            firstNameError={authorComposerUpdateStore.errors.firstName?.message}
            onFirstNameChange={(value: string) => {
              authorComposerUpdateStore.setFirstNameValue(value);
            }}
            firstName={authorComposerUpdateStore.entityValues.firstName}
            lastNameError={authorComposerUpdateStore.errors.lastName?.message}
            onLastNameChange={(value: string) => {
              authorComposerUpdateStore.setLastNameValue(value);
            }}
            lastName={authorComposerUpdateStore.entityValues.lastName}
            genderError={authorComposerUpdateStore.errors.gender?.message}
            genderOptions={authorComposerUpdateStore.getGenderOptions()}
            onGenderChange={(value: Gender | "") => {
              authorComposerUpdateStore.setGenderValue(value);
            }}
            gender={authorComposerUpdateStore.entityValues.gender}
            taxIdTypeError={authorComposerUpdateStore.errors.taxIdType?.message}
            taxIdTypeOptions={authorComposerUpdateStore.getTaxIdTypeOptions()}
            onTaxIdTypeChange={(value: TaxIdType | "") => {
              authorComposerUpdateStore.setTaxIdTypeValue(value);
            }}
            taxIdType={authorComposerUpdateStore.entityValues.taxIdType}
            taxIdError={authorComposerUpdateStore.errors.taxId?.message}
            onTaxIdChange={(value: string) => {
              authorComposerUpdateStore.setTaxIdValue(value);
            }}
            taxId={authorComposerUpdateStore.entityValues.taxId}
            emailError={authorComposerUpdateStore.errors.email?.message}
            onEmailChange={(value: string) => {
              authorComposerUpdateStore.setEmailValue(value);
            }}
            email={authorComposerUpdateStore.entityValues.email}
            phoneError={authorComposerUpdateStore.errors.phone?.message}
            onPhoneChange={(value: string) => {
              authorComposerUpdateStore.setPhoneValue(value);
            }}
            phone={authorComposerUpdateStore.entityValues.phone}
            nationalityError={
              authorComposerUpdateStore.errors.nationality?.message
            }
            onNationalityChange={(value) => {
              authorComposerUpdateStore.setNationalityValue(value);
            }}
            nationalitySelectedValue={
              authorComposerUpdateStore.entityValues.showNationality
            }
            onNationalitySelected={(value) => {
              authorComposerUpdateStore.setSelectedNationality(value);
            }}
            nationalityOptions={authorComposerUpdateStore.getNationalityOptions()}
            dateOfBirthError={
              authorComposerUpdateStore.errors.dateOfBirth?.message
            }
            onDateOfBirthChange={(value) => {
              authorComposerUpdateStore.setDateOfBirthValue(value);
            }}
            dateOfBirth={authorComposerUpdateStore.entityValues.dateOfBirth}
            streetAndNumberError={
              authorComposerUpdateStore.errors.streetAndNumber?.message
            }
            onStreetAndNumberChange={(value: string) => {
              authorComposerUpdateStore.setStreetAndNumberValue(value);
            }}
            streetAndNumber={
              authorComposerUpdateStore.entityValues.streetAndNumber
            }
            floorError={authorComposerUpdateStore.errors.floor?.message}
            onFloorChange={(value: string) => {
              authorComposerUpdateStore.setFloorValue(value);
            }}
            floor={authorComposerUpdateStore.entityValues.floor}
            suiteError={authorComposerUpdateStore.errors.suite?.message}
            onSuiteChange={(value: string) => {
              authorComposerUpdateStore.setSuiteValue(value);
            }}
            suite={authorComposerUpdateStore.entityValues.suite}
            postalCodeError={
              authorComposerUpdateStore.errors.postalCode?.message
            }
            onPostalCodeChange={(value: string) => {
              authorComposerUpdateStore.setPostalCodeValue(value);
            }}
            postalCode={authorComposerUpdateStore.entityValues.postalCode}
            countryError={authorComposerUpdateStore.errors.country?.message}
            onCountryChange={(value) => {
              authorComposerUpdateStore.setCountryValue(value);
            }}
            countrySelectedValue={
              authorComposerUpdateStore.entityValues.showCountry
            }
            onCountrySelected={(value) => {
              authorComposerUpdateStore.setSelectedCountry(value);
            }}
            countryOptions={authorComposerUpdateStore.getCountryOptions()}
            provinceError={authorComposerUpdateStore.errors.province?.message}
            onProvinceChange={(value) => {
              authorComposerUpdateStore.setProvinceValue(value);
            }}
            provinceSelectedValue={
              authorComposerUpdateStore.entityValues.showProvince
            }
            onProvinceSelected={(value) => {
              authorComposerUpdateStore.setSelectedProvince(value);
            }}
            provinceOptions={authorComposerUpdateStore.getProvinceOptions()}
            cityError={authorComposerUpdateStore.errors.city?.message}
            onCityChange={(value: string) => {
              authorComposerUpdateStore.setCityValue(value);
            }}
            city={authorComposerUpdateStore.entityValues.city}
            akaError={authorComposerUpdateStore.errors.aka?.message}
            onAkaChange={(value: string) => {
              authorComposerUpdateStore.setAkaValue(value);
            }}
            aka={authorComposerUpdateStore.entityValues.aka}
            ipiError={authorComposerUpdateStore.errors.ipi?.message}
            onIpiChange={(value: string) => {
              authorComposerUpdateStore.setIpiValue(value);
            }}
            ipi={authorComposerUpdateStore.entityValues.ipi}
            recordId={
              authorComposerUpdateStore.authorComposerInstance?.id || ""
            }
            hasPendingChanges={authorComposerUpdateStore.hasPendingChanges}
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={authorComposerUpdateStore.saveDisabled}
            onSave={() => authorComposerUpdateStore.saveAuthorComposer()}
            onCancel={() => authorComposerUpdateStore.cancelAuthorComposer()}
          />
          <ErrorDialog
            open={authorComposerUpdateStore.errorDialogOpened}
            title={authorComposerUpdateStore.errorTitle}
            description={authorComposerUpdateStore.errorDescription}
            onClose={() => authorComposerUpdateStore.closeErrorDialog()}
          />
          <ConfirmCancelDialog
            open={authorComposerUpdateStore.confirmDialogOpened}
            title={authorComposerUpdateStore.confirmDialogTitle}
            description={authorComposerUpdateStore.confirmDialogDescription}
            onConfirm={() => authorComposerUpdateStore.executeConfirmedAction()}
            onCancel={() => authorComposerUpdateStore.closeConfirmDialog()}
          />
        </>
      );
    }
  );
